import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { connectSocket, emitSocket, onSocket } from "../../services/socketService";
import { getGameDef } from "../../services/crmServices/gameService";
import { Swords, X, Check } from "lucide-react";

const EXPIRY_MS = 30 * 1000;

// Listens globally for incoming game:challenge_received events and shows a
// floating toast. Mounted once from Layout.jsx.
export function IncomingChallengeToast() {
    const [challenges, setChallenges] = useState([]); // [{ id, fromUserName, gameId, expiresAt }]
    const navigate = useNavigate();

    useEffect(() => {
        connectSocket(); // ensure connected

        const offReceived = onSocket("game:challenge_received", ({ challenge }) => {
            setChallenges((prev) => [
                ...prev,
                {
                    id: challenge.id,
                    fromUserName: challenge.fromUserName,
                    gameId: challenge.gameId,
                    expiresAt: Date.now() + EXPIRY_MS
                }
            ]);
        });

        // When the match actually starts, dismiss any pending challenge from that opponent
        const offStarted = onSocket("game:match_started", () => {
            setChallenges([]);
            navigate("/games");
        });

        const offDecline = onSocket("game:challenge_cancelled", ({ challengeId }) => {
            setChallenges((prev) => prev.filter((c) => c.id !== challengeId));
        });

        return () => {
            offReceived?.();
            offStarted?.();
            offDecline?.();
        };
    }, [navigate]);

    // Tick to expire old invites
    useEffect(() => {
        if (challenges.length === 0) return;
        const id = setInterval(() => {
            setChallenges((prev) => prev.filter((c) => c.expiresAt > Date.now()));
        }, 1000);
        return () => clearInterval(id);
    }, [challenges.length]);

    const respond = (challenge, accept) => {
        emitSocket("game:respond", { challengeId: challenge.id, accept });
        setChallenges((prev) => prev.filter((c) => c.id !== challenge.id));
        if (accept) navigate("/games");
    };

    if (challenges.length === 0) return null;

    return (
        <div className="fixed top-4 right-4 z-[70] space-y-2 max-w-sm">
            {challenges.map((c) => {
                const game = getGameDef(c.gameId);
                const secondsLeft = Math.max(0, Math.round((c.expiresAt - Date.now()) / 1000));
                return (
                    <div key={c.id} className="bg-white rounded-xl shadow-2xl border-2 border-pink-300 overflow-hidden animate-in slide-in-from-right">
                        <div className="bg-gradient-to-r from-pink-500 to-purple-600 px-3 py-2 flex items-center gap-2 text-white">
                            <Swords className="h-4 w-4" />
                            <span className="text-xs font-bold uppercase tracking-wide">Game Challenge</span>
                            <span className="ml-auto text-[10px] opacity-80 tabular-nums">{secondsLeft}s</span>
                        </div>
                        <div className="px-3 py-3">
                            <p className="text-sm text-gray-900">
                                <span className="font-bold">{c.fromUserName}</span> challenges you to
                            </p>
                            <p className="text-base font-semibold text-gray-900 mt-0.5">
                                {game?.icon} {game?.name || c.gameId}
                            </p>
                            <div className="flex gap-2 mt-3">
                                <button
                                    onClick={() => respond(c, true)}
                                    className="flex-1 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-md flex items-center justify-center gap-1"
                                >
                                    <Check className="h-3.5 w-3.5" />
                                    Accept
                                </button>
                                <button
                                    onClick={() => respond(c, false)}
                                    className="flex-1 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold rounded-md flex items-center justify-center gap-1"
                                >
                                    <X className="h-3.5 w-3.5" />
                                    Decline
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
